import { SEO, pageSEO } from '../components/shared/SEO';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, Phone, MapPin, Send, Clock, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { PageHeader } from '../components/shared/SectionHeader';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { Toaster, toast } from 'sonner';

const contactInfo = [
  {
    icon: Phone,
    title: 'Phone',
    content: '(608) 987-8000',
    link: 'tel:+16089878000',
  },
  {
    icon: Mail,
    title: 'Email',
    content: 'info@guideproinc.com',
    link: 'mailto:info@guideproinc.com',
  },
  {
    icon: MapPin,
    title: 'Office',
    content: '110 East Main Street\nSuite 722, Madison\nWisconsin 53703, USA',
  },
  {
    icon: Clock,
    title: 'Hours',
    content: 'Mon–Fri: 09:00 am – 05:00 pm\nSat–Sun: Closed',
  },
];

const serviceOptions = [
  'PolicyCenter Implementation',
  'ClaimCenter Implementation',
  'BillingCenter Implementation',
  'Cloud Migration',
  'QA & Testing',
  'Staffing & Talent',
  'Other',
];

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Prepare the data for the API
      const submitData = {
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        message: formData.message,
        company: formData.company || null,
        phone: formData.phone || null,
        service: formData.service || null,
      };

      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setIsSubmitting(false);
      setIsSubmitted(true);
      toast.success('Message sent successfully! We\'ll be in touch soon.');

      // Reset form after success
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        phone: '',
        service: '',
        message: '',
      });

      // Reset success state after 5 seconds
      setTimeout(() => setIsSubmitted(false), 5000);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setIsSubmitting(false);
      toast.error('Failed to send message. Please try again.');
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO {...pageSEO.contact} />
      <Toaster position="top-right" />
      <PageHeader
        label="Get in Touch"
        title="Contact Us"
        description="Ready to transform your insurance operations? Let's discuss how Guidepro can help you achieve your goals."
      />

      <section className="py-20 lg:py-32 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <AnimatedSection>
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">
                  Let's Talk
                </h3>
                <p className="text-slate-600 dark:text-slate-300 mb-8 leading-relaxed">
                  Whether you're planning a new implementation or looking to optimize 
                  your existing Guidewire platform, our experts are here to help.
                </p>

                <div className="space-y-6">
                  {contactInfo.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start gap-4"
                    >
                      <div className="w-12 h-12 rounded-xl bg-sky-100 dark:bg-sky-900/50 flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-5 h-5 text-sky-600 dark:text-sky-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 dark:text-white mb-1">
                          {item.title}
                        </h4>
                        {item.link ? (
                          <a 
                            href={item.link}
                            className="text-slate-600 dark:text-slate-300 hover:text-sky-600 dark:hover:text-sky-400 transition-colors whitespace-pre-line"
                          >
                            {item.content}
                          </a>
                        ) : (
                          <p className="text-slate-600 dark:text-slate-300 whitespace-pre-line">
                            {item.content}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </AnimatedSection>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <AnimatedSection direction="right">
                <div className="bg-slate-50 dark:bg-slate-800 rounded-2xl p-8 lg:p-10">
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">
                    Send Us a Message
                  </h3>

                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First Name *</Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          placeholder="John"
                          className="bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                          data-testid="input-first-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          placeholder="Doe"
                          className="bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                          data-testid="input-last-name"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="email">Work Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="john@company.com"
                          className="bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                          data-testid="input-email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+1 (555) 000-0000"
                          className="bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                          data-testid="input-phone"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="company">Company *</Label>
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          required
                          placeholder="Your Company"
                          className="bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                          data-testid="input-company"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="service">Service of Interest</Label>
                        <Select onValueChange={handleServiceChange} value={formData.service}>
                          <SelectTrigger className="bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-white" data-testid="select-service">
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent>
                            {serviceOptions.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="Tell us about your project..."
                        rows={5}
                        className="bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-white resize-none"
                        data-testid="input-message"
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full sm:w-auto bg-sky-600 hover:bg-sky-700 text-white h-12 px-8"
                      data-testid="submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                            className="w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"
                          />
                          Sending...
                        </>
                      ) : isSubmitted ? (
                        <>
                          <CheckCircle className="w-5 h-5 mr-2" />
                          Sent Successfully!
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="relative h-96 bg-slate-200 dark:bg-slate-800">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <MapPin className="w-12 h-12 text-sky-500 mx-auto mb-4" />
            <p className="text-slate-600 dark:text-slate-300 font-medium">
              110 East Main Street, Suite 722, Madison, Wisconsin 53703, USA
            </p>
          </div>
        </div>
        {/* Actual map would be integrated here */}
        <div className="absolute inset-0 bg-gradient-to-t from-white dark:from-slate-900 via-transparent to-transparent" />
      </section>
    </div>
  );
};

export default Contact;
